import { useMemo, useState } from 'react';
import { api, ApiError, formatDateTime, money } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { DiasporaRegistryEntry, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

/**
 * Diaspora registry — outreach console + investor referral (spec:
 * "DiasporaRegistry — location, skills, investment interest — feeds M12
 * investor pipeline"). Registration/updates are self-service (T2 diaspora
 * citizens, not staff — no create form here on purpose); staff read the
 * outreach list and trigger the M12 referral for entries that opted in.
 */
export function DiasporaRegistry() {
  const { persona } = useAuth();
  const canRefer = (persona?.roles.includes('CONSULAR_OFFICER') || persona?.roles.includes('MISSION_HEAD')) ?? false;
  const canView = canRefer || (persona?.roles.includes('PROTOCOL_OFFICER') ?? false);
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [country, setCountry] = useState('');
  const [investmentInterest, setInvestmentInterest] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (appliedQ) params.set('q', appliedQ);
    if (country) params.set('country', country);
    if (investmentInterest) params.set('investmentInterest', investmentInterest);
    return `/api/v1/m42/diaspora-registry?${params.toString()}`;
  }, [appliedQ, country, investmentInterest]);

  const listQ = useApiQuery<Page<DiasporaRegistryEntry>>(path);

  return (
    <div>
      <PageHeader
        title="Diaspora Registry"
        subtitle="Voluntary registry of Seychellois abroad — outreach contact list & investor-interest referral to M12."
      />

      {!canView ? <RoleNotice roles={['CONSULAR_OFFICER', 'MISSION_HEAD', 'PROTOCOL_OFFICER']} /> : null}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedQ(q);
          }}
        >
          <input className="field-input !w-48" placeholder="Name or NIN…" value={q} onChange={(e) => setQ(e.target.value)} />
          <input className="field-input !w-40" placeholder="Country of residence…" value={country} onChange={(e) => setCountry(e.target.value)} />
          <select className="field-input !w-auto" value={investmentInterest} onChange={(e) => setInvestmentInterest(e.target.value)}>
            <option value="">Any investment interest</option>
            <option value="true">Investment-interested only</option>
            <option value="false">No investment interest</option>
          </select>
          <button type="submit" className="btn-secondary">
            Search
          </button>
        </form>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No diaspora registry entries match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Name</th>
                <th className="table-head">NIN</th>
                <th className="table-head">Location</th>
                <th className="table-head">Occupation</th>
                <th className="table-head">Investment interest</th>
                <th className="table-head">M12 referral</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((d) => (
                <tr key={d.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(d.id)}>
                  <td className="table-cell font-medium">{d.fullName}</td>
                  <td className="table-cell font-mono text-xs">{d.nin}</td>
                  <td className="table-cell text-xs text-slate-500">
                    {d.city ? `${d.city}, ` : ''}
                    {d.countryOfResidence}
                  </td>
                  <td className="table-cell text-xs text-slate-500">{d.occupation ?? '—'}</td>
                  <td className="table-cell">
                    {d.investmentInterest ? <StatusBadge status="REQUESTED" /> : <span className="text-xs text-slate-400">—</span>}
                  </td>
                  <td className="table-cell">
                    {d.referredToM12 ? <StatusBadge status="DONE" /> : <span className="text-xs text-slate-400">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <EntryDetailModal
          id={selectedId}
          canRefer={canRefer}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            setSelectedId(null);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function EntryDetailModal({
  id,
  canRefer,
  onClose,
  onChanged,
}: {
  id: string;
  canRefer: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const detailQ = useApiQuery<DiasporaRegistryEntry>(`/api/v1/m42/diaspora-registry/${id}`);
  const d = detailQ.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refer = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m42/diaspora-registry/${id}/refer-investment`);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Referral failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={d ? d.fullName : 'Diaspora registry entry'} onClose={onClose} wide>
      {detailQ.error ? <ErrorNote message={detailQ.error} /> : null}
      {detailQ.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {d ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="NIN" value={d.nin} mono />
            <Detail label="Status" value={<StatusBadge status={d.active ? 'ACTIVE' : 'WITHDRAWN'} />} />
            <Detail label="Location" value={`${d.city ? `${d.city}, ` : ''}${d.countryOfResidence}`} />
            <Detail label="Contact email" value={d.contactEmail} />
            {d.contactPhone ? <Detail label="Contact phone" value={d.contactPhone} /> : null}
            <Detail label="Occupation" value={d.occupation ?? '—'} />
            <Detail label="Consent to contact" value={d.consentToContact ? 'Yes' : 'No'} />
          </div>

          {d.skills.length > 0 ? (
            <Detail label="Skills" value={d.skills.join(', ')} />
          ) : null}
          {d.sectorsOfInterest.length > 0 ? (
            <Detail label="Sectors of interest" value={d.sectorsOfInterest.join(', ')} />
          ) : null}

          {d.investmentInterest && d.investmentDetails ? (
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3.5 py-2.5 text-sm text-indigo-900">
              <div className="font-semibold">Investment interest</div>
              <div className="mt-1 grid grid-cols-2 gap-2 sm:grid-cols-3">
                <Detail label="Sector" value={d.investmentDetails.sector.replaceAll('_', ' ')} />
                <Detail label="Capex" value={money(d.investmentDetails.capexAmount, d.investmentDetails.capexCurrency)} />
              </div>
              <p className="mt-2">{d.investmentDetails.description}</p>
            </div>
          ) : null}

          {d.referredToM12 ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-800">
              Already referred to M12 — project reference <span className="font-mono">{d.m12ProjectRef}</span> on {formatDateTime(d.referredAt)}
            </div>
          ) : d.investmentInterest ? (
            canRefer ? (
              <div className="border-t border-slate-100 pt-4">
                <button className="btn-primary" disabled={busy} onClick={refer}>
                  {busy ? 'Referring…' : 'Refer investment interest to M12'}
                </button>
              </div>
            ) : (
              <RoleNotice roles={['CONSULAR_OFFICER', 'MISSION_HEAD']} />
            )
          ) : (
            <p className="text-xs text-slate-400">This citizen has not indicated investment interest — nothing to refer.</p>
          )}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">
            Registered {formatDateTime(d.createdAt)} · last updated {formatDateTime(d.updatedAt)}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
